/**
 * Nexsoss design tokens.
 *
 * Locked values from initial_plan/SPEC.md §5.2 are mirrored here in TypeScript.
 * The .css counterpart in tokens.css must remain in sync; runtime CSS custom
 * properties are the authoritative source.
 */

export const NX_TOKENS = {
  bg: '#0a0b0d',
  bg2: '#0f1117',
  bg3: '#141820',
  card: '#111418',

  border: 'rgba(255, 255, 255, 0.07)',

  blue: '#2e8fff',
  cyan: '#00e5cc',
  grad: 'linear-gradient(135deg, #2e8fff, #00e5cc)',

  text: '#e8ecf0',
  muted: '#7a8a9a',
  white: '#ffffff',

  error: '#ff4d6a',
  warning: '#ffb547',
  moving: '#ffb547',
  success: '#00e5cc',
  info: '#2e8fff',

  fontHeading: "'Rajdhani', sans-serif",
  fontBody: "'DM Sans', sans-serif",

  radius: '8px',
} as const;

/**
 * Status colours, as CSS custom-property REFERENCES rather than the
 * `NX_TOKENS` hex literals. These are painted (as `--nx-chip-color`)
 * onto every StatusChip; the literals are the dark palette only, so in
 * light mode an ACTIVE chip was #00e5cc text on white (1.6:1) and an
 * OFFLINE chip was 20%-white on white — invisible. A `var()` resolves
 * against whichever theme `<html data-theme>` carries.
 */
export const NX_STATUS_COLOR = {
  active: 'var(--nx-cyan)',
  idle: 'var(--nx-muted)',
  error: 'var(--nx-error)',
  charging: 'var(--nx-blue)',
  warning: 'var(--nx-warning)',
  offline: 'var(--nx-status-offline)',
  // Distinct from BOTH `warning` (a completed task isn't an issue
  // needing attention) AND `active` (cyan was reused for both at
  // first — confirmed too close to tell apart at a glance, especially
  // with `active`'s pulse animation being the only other cue). Blue
  // reads as calm/neutral/"done" against cyan's energetic "live" tone.
  completed: 'var(--nx-blue)',
  // A door/lift-door mid-transit is transient and worth catching at a
  // glance across a whole table or the building map, not just another
  // `warning` (which also covers things that AREN'T actively changing
  // right now, e.g. a degraded-but-stable component) — `StatusChip`
  // gives this one a strong flash instead of the subtle dot-pulse
  // `active`/others get.
  moving: 'var(--nx-moving)',
} as const;

export type StatusKind = keyof typeof NX_STATUS_COLOR;
